import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  ButtonGroup,
  DrawerBody,
  DrawerFooter,
  FormController,
  FormLabel,
  TextField,
  SkeletonBlock,
  Typography
} from '@one-thd/sui-atomic-components';
import { ProductInfo } from '../shared/ProductInfo';
import { DELIVERY_FULFILLMENT, SCREENS, CREATE_A_QUOTE_X_OR_CANCEL_BUTTON, QUOTE_NAME_MAX_LENGTH } from '../shared/Constants';
import { useCreateQuote } from '../hooks/useQuotes';
import { useCustomerInformation } from '../hooks/useCustomerInformation';
import {
  triggerCloseQuoteAnalytics, triggerErrorAnalytics, getQuoteNameError, triggerQuoteAddAnalytics
} from '../../utils/helpers';
import { DeliveryZipCode } from './DeliveryZipCode';

const CreateNewQuoteDrawer = ({
  location,
  onClose,
  products,
  setError,
  setQuoteId,
  setQuoteName,
  projectQuoteData = {},
  productExceptions = [],
  setQuoteOriginAnalytic,
  setDeliveryZip,
  setScreen,
  quoteTimeTrackingRef,
  totalNbrOfQuotes = 0
}) => {
  const [quoteNameError, setQuoteNameError] = useState(null);
  const [newQuoteName, setNewQuoteName] = useState('');
  const { deliveryZip, localStoreId, applianceDeliveryStore } = location || {};
  const customerInfo = useCustomerInformation();
  const { projectId, projectName, projectZipCode } = projectQuoteData || {};

  const { ERROR_SCREEN, SUCCESS } = SCREENS;

  const showZipCode = products?.some((product) => {
    return DELIVERY_FULFILLMENT.includes(product?.fulfillmentMethod);
  });
  const [isDisabled, setDisabled] = useState(showZipCode && !deliveryZip);

  const [createQuote, quoteResponse] = useCreateQuote({
    items: products,
    ...customerInfo,
    quoteName: newQuoteName,
    localStoreId,
    applianceDeliveryStore,
    deliveryZip,
    isNew: true,
    projectId
  });

  const { loading, data, error } = quoteResponse;

  const createANewQuote = () => {
    createQuote();
  };

  const handleQuoteNameInputChange = (value) => {
    const updatedValue = typeof value === 'string'
      ? value.slice(0, QUOTE_NAME_MAX_LENGTH)
      : '';
    setNewQuoteName(updatedValue);
    setQuoteNameError(getQuoteNameError(updatedValue));
  };

  const triggerAnalytics = () => {
    triggerQuoteAddAnalytics('add-to-quote.newQuoteAdd', {
      quoteId: data?.createQuote?.cartId,
      startTime: quoteTimeTrackingRef?.current?.startTime,
      products,
      productExceptions
    });
  };

  const handleClose = () => {
    onClose();
    triggerCloseQuoteAnalytics(CREATE_A_QUOTE_X_OR_CANCEL_BUTTON);
  };

  useEffect(() => {
    if (data && !loading && !error) {
      setQuoteName(newQuoteName);
      setNewQuoteName('');
      setQuoteId(data?.createQuote?.cartId);
      setScreen(SUCCESS);
      triggerAnalytics();
      setQuoteOriginAnalytic('create-a-quote');
    } else if (error) {
      triggerErrorAnalytics(error);
      setError(error);
      setScreen(ERROR_SCREEN);
    }
  }, [data, loading, error]);

  if (loading) {
    return (
      <DrawerBody>
        <SkeletonBlock />
      </DrawerBody>
    );
  }

  return (
    <>
      <DrawerBody>
        <ProductInfo products={products} storeId={localStoreId} />
        <div className="sui-my-6 sui-border-b-1 sui-border-primary sui-border-solid" />
        {showZipCode && (
          <div className="sui-mb-6">
            {(!projectZipCode && totalNbrOfQuotes === 0) ? (
              <DeliveryZipCode
                deliveryZip={deliveryZip}
                setDeliveryZip={setDeliveryZip}
                setDisabled={setDisabled}
              />
            ) : (
              <>
                <Typography>Delivery Zip Code</Typography>
                <Typography weight="bold">{deliveryZip}</Typography>
              </>
            )}
          </div>
        )}
        <FormController className="sui-w-full">
          <FormLabel
            id="create-quote"
            htmlFor="create-quote-input"
            className="sui-cursor-default sui-text-base"
          >
            Quote Name *
          </FormLabel>
          <div>
            <TextField
              fullWidth
              id="create-quote-input"
              placeholder="Enter a Quote Name"
              value={newQuoteName}
              onChange={(evt) => {
                handleQuoteNameInputChange(evt?.target?.value);
              }}
              status={quoteNameError ? 'error' : null}
              statusMessage={quoteNameError}
            />
          </div>
        </FormController>
        {projectName && (
          <div className="sui-mt-6">
            <Typography>Project</Typography>
            <Typography weight="bold">{projectName}</Typography>
          </div>
        )}
      </DrawerBody>
      <DrawerFooter>
        <ButtonGroup orientation="vertical">
          <Button
            fullWidth
            variant="primary"
            onClick={createANewQuote}
            disabled={!newQuoteName?.length || !!quoteNameError || isDisabled}
            data-testid="create-new-quote"
          >
            Add to Quote
          </Button>
          <Button fullWidth variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
        </ButtonGroup>
      </DrawerFooter>
    </>
  );
};

CreateNewQuoteDrawer.propTypes = {
  location: PropTypes.shape({
    localStoreId: PropTypes.string,
    deliveryZip: PropTypes.string
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      itemId: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      fulfillmentLocation: PropTypes.string.isRequired,
      fulfillmentMethod: PropTypes.string.isRequired,
      identifiers: PropTypes.shape({
        brandName: PropTypes.string,
        productLabel: PropTypes.string
      }).isRequired,
      pricing: PropTypes.shape({
        value: PropTypes.number
      }).isRequired,
      media: PropTypes.shape({
        images: PropTypes.arrayOf(
          PropTypes.shape({
            altText: PropTypes.string,
            sizes: PropTypes.arrayOf(PropTypes.string),
            subType: PropTypes.string,
            type: PropTypes.string,
            url: PropTypes.string
          })
        )
      })
    })
  ).isRequired,
  setError: PropTypes.func.isRequired,
  setQuoteId: PropTypes.func.isRequired,
  setQuoteName: PropTypes.func.isRequired,
  setScreen: PropTypes.func.isRequired,
  setDeliveryZip: PropTypes.func.isRequired,
  projectQuoteData: PropTypes.shape({
    projectId: PropTypes.string,
    projectZipCode: PropTypes.string,
    projectName: PropTypes.string
  }),
  productExceptions: PropTypes.arrayOf(
    PropTypes.shape({
      flag: PropTypes.string.isRequired,
      fulfillment: PropTypes.shape({}),
      identifiers: PropTypes.shape({}),
      pricing: PropTypes.shape({}),
      itemId: PropTypes.string,
      id: PropTypes.string,
      quantity: PropTypes.number
    })
  ),
  setQuoteOriginAnalytic: PropTypes.func.isRequired,
  quoteTimeTrackingRef: PropTypes.shape({
    current: PropTypes.shape({
      startTime: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])])
    })
  }).isRequired,
  totalNbrOfQuotes: PropTypes.number
};

CreateNewQuoteDrawer.displayName = 'CreateNewQuoteDrawer';

export { CreateNewQuoteDrawer };
